/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { formatComptableMaisPasTrop } from "../../../services/function";

/**
 * Kit de présentation partagé par tous les tableaux de l'application.
 * Chaque tableau garde sa logique métier et se contente d'utiliser ces briques
 * pour obtenir un rendu identique : table sur grand écran, cartes sur mobile.
 */

type Column = { header: string; accessor: string };

export type IconButtonTone =
  | "cyan"
  | "blue"
  | "purple"
  | "indigo"
  | "emerald"
  | "danger";

const TONE_CLASSES: Record<IconButtonTone, string> = {
  cyan: "border-slate-200 bg-white text-slate-500 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700",
  blue: "border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700",
  purple:
    "border-slate-200 bg-white text-slate-500 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700",
  indigo:
    "border-slate-200 bg-white text-slate-500 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700",
  emerald:
    "border-slate-200 bg-white text-slate-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700",
  danger:
    "border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100 hover:text-red-700",
};

export const IconButton = ({
  label,
  tone,
  onClick,
  children,
}: {
  label: string;
  tone: IconButtonTone;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className={`inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border transition-colors focus-visible:ring-2 focus-visible:ring-indigo-200 focus-visible:outline-none ${TONE_CLASSES[tone]}`}
  >
    {children}
  </button>
);

/** Réserve la place d'un bouton absent pour garder les colonnes alignées. */
export const IconButtonSpacer = () => (
  <span aria-hidden="true" className="inline-block size-9 shrink-0" />
);

const iconClass = "size-4.5";

export const EyeIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

export const DownloadIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

export const TrashIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export const PencilIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

export const DocumentIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

export const LedgerIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

export const RefundIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

type BadgeTone =
  | "slate"
  | "indigo"
  | "amber"
  | "emerald"
  | "red"
  | "blue"
  | "purple";

const BADGE_TONES: Record<BadgeTone, string> = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-100",
  amber: "bg-amber-100 text-amber-800 ring-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  red: "bg-red-50 text-red-700 ring-red-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  purple: "bg-purple-50 text-purple-700 ring-purple-200",
};

export const Badge = ({
  children,
  tone = "slate",
  icon,
}: {
  children: React.ReactNode;
  tone?: BadgeTone;
  icon?: React.ReactNode;
}) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ring-1 ring-inset ${BADGE_TONES[tone]}`}
  >
    {icon}
    {children}
  </span>
);

/** Étiquette en majuscules utilisée dans les cartes mobiles et les en-têtes. */
export const MicroLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
    {children}
  </span>
);

/**
 * Conteneur de défilement commun. `scrollRef` sert aux tableaux qui remontent
 * en haut de liste lors d'une recherche.
 */
export const TableFrame = ({
  scrollRef,
  children,
}: {
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) => (
  <div className="flex min-h-0 flex-1 flex-col">
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
      {children}
    </div>
  </div>
);

export const DesktopTable = ({ children }: { children: React.ReactNode }) => (
  <table className="hidden w-full border-separate border-spacing-0 lg:table">
    {children}
  </table>
);

export const Th = ({
  children,
  align = "left",
  className = "",
  width,
}: {
  children?: React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  width?: string;
}) => (
  <th
    scope="col"
    style={width ? { width } : undefined}
    className={`border-b border-slate-200 bg-slate-50/95 px-4 py-3 font-Nunito text-[11px] font-bold tracking-wider text-slate-500 uppercase backdrop-blur ${
      align === "right"
        ? "text-right"
        : align === "center"
          ? "text-center"
          : "text-left"
    } ${className}`}
  >
    {children}
  </th>
);

export const Td = ({
  children,
  align = "left",
  className = "",
  colSpan,
  onClick,
  dense = false,
  indent = false,
}: {
  children?: React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  colSpan?: number;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  /** Ligne liée (avoir, remboursement) : padding vertical réduit. */
  dense?: boolean;
  /** Décale le contenu pour signaler une ligne rattachée à la précédente. */
  indent?: boolean;
}) => (
  <td
    colSpan={colSpan}
    onClick={onClick}
    className={`border-b border-slate-100 align-middle text-sm 2xl:text-[15px] ${
      dense ? "py-2.5" : "py-3"
    } ${indent ? "pr-4 pl-8" : "px-4"} ${
      align === "right"
        ? "text-right tabular-nums"
        : align === "center"
          ? "text-center"
          : "text-left"
    } ${className}`}
  >
    {children}
  </td>
);

/** Classes de la ligne principale, selon son état de mise en avant. */
export const rowClasses = ({
  highlighted = false,
  muted = false,
  selected = false,
  clickable = true,
}: {
  highlighted?: boolean;
  muted?: boolean;
  selected?: boolean;
  clickable?: boolean;
}) =>
  `group transition-colors ${clickable ? "cursor-pointer" : ""} ${
    highlighted
      ? "bg-amber-50/70 hover:bg-amber-100/70"
      : selected
        ? "bg-emerald-50/70 hover:bg-emerald-100/60"
        : muted
          ? "bg-slate-50 text-slate-400"
          : "hover:bg-indigo-50/60"
  }`;

export const cardClasses = ({
  highlighted = false,
  muted = false,
  selected = false,
}: {
  highlighted?: boolean;
  muted?: boolean;
  selected?: boolean;
}) =>
  `cursor-pointer rounded-xl border p-3.5 transition-colors ${
    highlighted
      ? "border-amber-200 bg-amber-50/70"
      : selected
        ? "border-emerald-200 bg-emerald-50/60"
        : muted
          ? "border-slate-200 bg-slate-50"
          : "border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/40"
  }`;

export const MobileCardList = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <ul className="flex flex-col gap-2.5 p-3 font-Nunito sm:p-4 lg:hidden">
    {children}
  </ul>
);

/**
 * Grille de paires libellé / valeur affichée dans une carte mobile.
 */
export const MobileFieldGrid = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-slate-200/70 pt-3 sm:grid-cols-3">
    {children}
  </dl>
);

export const MobileField = ({
  label,
  value,
  numeric = false,
  muted = false,
}: {
  label: string;
  value: React.ReactNode;
  numeric?: boolean;
  muted?: boolean;
}) => (
  <div className="min-w-0">
    <dt>
      <MicroLabel>{label}</MicroLabel>
    </dt>
    <dd
      className={`mt-0.5 text-sm font-medium ${numeric ? "tabular-nums" : ""} ${
        muted ? "text-slate-400" : "text-slate-700"
      }`}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </dd>
  </div>
);

export const MobileCardFooter = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200/70 pt-3">
    {children}
  </div>
);

/**
 * Bloc "aucune donnée", utilisable hors tableau (les tableaux ne rendent la
 * table que lorsqu'il y a des lignes).
 */
export const TableEmptyState = ({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon?: React.ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
    <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-100">
      {icon ?? (
        <svg
          aria-hidden="true"
          className="size-7 text-slate-400"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      )}
    </div>
    <p className="mt-3 font-Nunito text-base font-semibold text-slate-700">
      {title}
    </p>
    <p className="mt-1 font-Nunito text-sm text-slate-500">{description}</p>
  </div>
);

export const emptyStateIcon = (path: string) => (
  <svg
    aria-hidden="true"
    className="size-7 text-slate-400"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

/**
 * Colonnes dont au moins une valeur est numérique : elles sont alignées à
 * droite et rendues en chiffres tabulaires.
 */
export const getNumericAccessors = (
  columns: Column[],
  data: Record<string, any>[],
) =>
  new Set(
    columns
      .filter((col) => data.some((row) => typeof row[col.accessor] === "number"))
      .map((col) => col.accessor),
  );

/**
 * Rendu par défaut d'une valeur de cellule : listes en pastilles, nombres
 * formatés, reste tel quel.
 */
export const renderCellValue = (value: any): React.ReactNode => {
  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((item: string, index: number) => (
          <Badge key={index} tone="blue">
            {item}
          </Badge>
        ))}
      </div>
    );
  }

  if (typeof value === "number") {
    return (
      <span className="font-semibold tabular-nums">
        {formatComptableMaisPasTrop(value)}
      </span>
    );
  }

  return value;
};

/** Pastille informative des lignes liées (avoirs, remboursements). */
export const LinkedRowSummary = ({
  label,
  tone = "amber",
  reference,
  amountLabel,
  amount,
}: {
  label: string;
  tone?: BadgeTone;
  reference: React.ReactNode;
  amountLabel: string;
  amount: React.ReactNode;
}) => (
  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm">
    <Badge tone={tone}>{label}</Badge>
    <span className="font-bold text-slate-800">{reference}</span>
    <span aria-hidden="true" className="text-slate-300">
      •
    </span>
    <span className="text-slate-600">
      {amountLabel} :{" "}
      <span className="font-semibold text-slate-800 tabular-nums">{amount}</span>
    </span>
  </div>
);

export const Checkbox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
}) => (
  <input
    type="checkbox"
    aria-label={label}
    checked={checked}
    onChange={onChange}
    onClick={(e) => e.stopPropagation()}
    className="size-4 cursor-pointer rounded border-slate-300 text-dca-blue accent-dca-blue focus:ring-2 focus:ring-indigo-200"
  />
);
